import React, { useContext, useState, useEffect } from 'react'
import { css } from '@emotion/core'
import { useTranslation } from 'react-i18next'
import { AccountContext } from '@/contexts/account'
import { wholeContainerStyle, mainColumnStyle, mainContainerStyle } from '@/constants/layout'
import * as api from '@/apis/aipass'
import { LoadingFull } from '@/components/molecules/loading-full'
import { Header } from '@/components/organisms/header'
import { SideMenu } from '@/components/organisms/side-menu'
import { BusinessStatus } from '@/components/organisms/dashboard/business-status'
import dayjs from 'dayjs'
import { useHistory } from 'react-router-dom'
import { useQuery } from '@/hooks/use-query'
import { sectionStyle } from '@/components/organisms/dashboard/style'
import { MessageBoardByCommonly } from '../organisms/dashboard/message-board-by-commonly'
import { MessageBoardByDaily } from '../organisms/dashboard/message-board-by-daily'
import { LoadingContent } from '../molecules/loading-content'
import { DashboardTotalResponse } from '@/models/dashboard/dashboard-total'
import { DashboardReservationResponse } from '@/models/dashboard/dashboard-reservation'
import { ReservationTable } from '@/components/organisms/dashboard/reservation-table'

export const Dashboard: React.FC = () => {
  const history = useHistory()
  const { t } = useTranslation()
  const query = useQuery()
  const [date, setDate] = useState(dayjs(query.get('date') || undefined))
  const { account, plugins } = useContext<any>(AccountContext)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [dashboardValue, setDashboardValue] = useState<DashboardTotalResponse | null>(null)
  const [dashboardNote, setDashboardNote] = useState<{ commonly: { note: string }; daily: { note: string } }>()
  const [isLoadingNote, setIsLoadingNote] = useState(false)
  const [isLoadingReservation, setIsLoadingReservation] = useState(false)
  const [dashboardReservation, setDashboardReservation] = useState<DashboardReservationResponse | null>(null)
  const refresh = async () => {
    console.log('refresh')
    setIsLoading(true)
    await Promise.all([fetchDashboardNote(), fetchAdminDashboard(), fetchDashboardReservation()])
    setIsLoading(false)
  }

  const fetchAdminDashboard = () => {
    api.fetchAdminDashboard(date).then(res => {
      setDashboardValue(res)
    })
  }

  const fetchDashboardNote = () => {
    setIsLoadingNote(true)
    api.fetchDashboardNote(date.format('YYYY-MM-DD')).then(res => {
      res && setDashboardNote(res)
      setIsLoadingNote(false)
    })
  }

  const fetchDashboardReservation = () => {
    setIsLoadingReservation(true)
    api.fetchDashboardReservation(date.format('YYYY-MM-DD')).then(res => {
      res && setDashboardReservation(res)
      setIsLoadingReservation(false)
    })
  }

  useEffect(() => {
    if (account && account?.hotel) {
      refresh()
      history.replace({ pathname: '/dashboard', search: `date=${date.format('YYYY-MM-DD')}` })
    }
  }, [account, date])

  return (
    <div css={wholeContainerStyle}>
      <SideMenu />
      <div className="mainColumn" css={mainColumnStyle}>
        <Header title={t('Dashboard')} />
        <div className="dashboardMainContainerStyle" css={dashboardMainContainerStyle}>
          <div css={leftContainerStyle}>
            {dashboardValue && (
              <BusinessStatus
                dailyTotal={dashboardValue.dailyTotal}
                cancel={dashboardValue.cancel}
                occ={dashboardValue.occ}
                price={dashboardValue.price}
                repeater={dashboardValue.repeater}
                date={date}
                setDate={setDate}
              />
            )}
          </div>
          <div css={rightContainerStyle}>
            <div css={css(sectionStyle, { position: 'relative' })}>
              <LoadingContent isLoading={isLoadingNote} />
              <MessageBoardByCommonly note={dashboardNote?.commonly.note || ''} setIsLoading={setIsLoadingNote} />
              <MessageBoardByDaily note={dashboardNote?.daily.note || ''} date={date} setIsLoading={setIsLoadingNote} />
            </div>
          </div>
        </div>
        <div css={fullWidthContainerStyle}>
          <LoadingContent isLoading={isLoadingReservation} />
          <ReservationTable data={dashboardReservation} onRefresh={() => fetchDashboardReservation()} />
        </div>
      </div>
      <LoadingFull isLoading={isLoading} />
    </div>
  )
}

const dashboardMainContainerStyle = css(mainContainerStyle, {
  display: 'flex',
  overflowX: 'scroll',
})

const leftContainerStyle = css({
  flex: 'auto',
  width: '66.5%',
  minWidth: 695,
  marginRight: 16,
})

const rightContainerStyle = css({
  width: 340,
  minWidth: 340,
})

const fullWidthContainerStyle = css({
  display: 'flex',
  overflowX: 'scroll',
  width: '100%',
  minWidth: '100%',
  padding: '0 24px',
  marginTop: '7px',
})
